import { openCalendly } from "../../utils/calendly";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

const StyledBox = styled("div")(({ theme }) => ({
  alignSelf: "center",
  width: "100%",
  marginTop: theme.spacing(8),
  borderRadius: (theme.vars || theme).shape.borderRadius,
  outline: "6px solid",
  outlineColor: "hsla(220, 25%, 80%, 0.2)",
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.grey[200],
  boxShadow: "0 0 12px 8px hsla(220, 25%, 80%, 0.2)",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(10),
  },
  ...theme.applyStyles("dark", {
    boxShadow: "0 0 24px 12px hsla(210, 100%, 25%, 0.2)",
    outlineColor: "hsla(220, 20%, 42%, 0.1)",
    borderColor: (theme.vars || theme).palette.grey[700],
  }),
}));

export default function Hero() {
  return (
    <Box
      id="hero"
      sx={(theme) => ({
        width: "100%",
        backgroundRepeat: "no-repeat",
        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)",
        ...theme.applyStyles("dark", {
          backgroundImage:
            "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)",
        }),
      })}
    >
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 16, sm: 23 },
          pb: { xs: 10, sm: 15 },
        }}
      >
        <Stack
          spacing={2}
          useFlexGap
          sx={{ alignItems: "center", width: { xs: "100%", sm: "70%" } }}
        >
          <Typography
            variant="h1"
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              fontSize: "clamp(2.5rem, 9vw, 3rem)",
              textAlign: "center",
            }}
          >
            Transform Your&nbsp;
            <Typography
              component="span"
              variant="h1"
              sx={(theme) => ({
                fontSize: "inherit",
                color: "secondary.main",
                ...theme.applyStyles("dark", {
                  color: "primary.light",
                }),
              })}
            >
              Sales Calls
            </Typography>
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              color: "text.secondary",
              width: { sm: "100%", md: "80%" },
              fontSize: { xs: "0.75rem", sm: ".875rem" },
            }}
          >
            Sales managers at companies using Savac Plus eliminate upwards of
            90% of the time previously spent on call review, while coaching more
            consistently than ever. The platform analyzes every recorded call
            automatically - scoring performance, flagging problem areas, and
            tracking rep improvement over time. New hires get up to speed
            significantly faster with automatic feedback on every call from day
            one.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            useFlexGap
            sx={{ pt: 2, width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={openCalendly}
              sx={{
                minWidth: { xs: "100%", sm: "180px" },
                backgroundImage: "none",
                backgroundColor: "hsl(210, 98%, 42%)",
                color: "white",
                border: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundImage: "none",
                  backgroundColor: "hsl(210, 100%, 35%)",
                  boxShadow: "none",
                },
              }}
            >
              Book a Demo
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => {
                const pricingSection = document.getElementById("pricing");
                if (pricingSection) {
                  pricingSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
              sx={(theme) => ({
                minWidth: { xs: "100%", sm: "180px" },
                ...theme.applyStyles("dark", {
                  borderColor: "rgba(255,255,255,0.4)",
                  "&:hover": { borderColor: "rgba(255,255,255,0.7)" },
                }),
              })}
            >
              View Plans
            </Button>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", mt: 2 }}
          >
            Free 30-minute demo • See it live with your own calls
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
              mt: 3,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.7rem",
                whiteSpace: "nowrap",
              }}
            >
              Trusted by
            </Typography>
            <Box
              component="img"
              src="/precoa.png"
              alt="Precoa"
              sx={{
                height: { xs: 24, sm: 28 },
                width: "auto",
                opacity: 0.85,
                filter: (theme) =>
                  theme.palette.mode === "dark"
                    ? "brightness(0) invert(1)"
                    : "none",
              }}
            />
          </Box>
        </Stack>
        <StyledBox id="image" sx={{ mt: { xs: 6, sm: 8 } }}>
          <img
            src="/salesperson_detail.png"
            alt="Sales call analyzer dashboard"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </StyledBox>
      </Container>
    </Box>
  );
}
